// Generative LLM hook — Qwen2.5-1.5B-Instruct via WebLLM.
//
// All web-llm imports are LAZY (dynamic import inside load()) so the 200KB
// runtime + the huge model don't end up in the default JS bundle. The user
// only pays the cost the moment they actually trigger Smart Mode.
//
// IndexedDB caches the model artifact after first download. Returning
// visitors skip the network entirely and just rebind to the cached weights
// (typically <2s including WebGPU pipeline compile).

import { useEffect, useRef, useState } from "react";

const MODEL_ID = "Qwen2.5-1.5B-Instruct-q4f16_1-MLC";

export type LLMState =
  | { status: "idle" }
  | { status: "loading"; progress: number; text: string }
  | { status: "ready"; warmed: boolean }
  | { status: "failed"; error: string };

export type Message = { role: "system" | "user" | "assistant"; content: string };

export type GenerateCallbacks = {
  onToken: (delta: string) => void;
  onFirstToken?: (firstTokenMs: number) => void;
  onDone?: (totalMs: number, tokenCount: number) => void;
  onError?: (err: string) => void;
};

// Singleton engine — survives unmounts so the model isn't re-bound when
// users navigate between routes. Module-level cache.
let enginePromise: Promise<unknown> | null = null;
let engineRef: unknown = null;

async function getEngine(
  onProgress: (p: number, text: string) => void,
): Promise<unknown> {
  if (engineRef) return engineRef;
  if (!enginePromise) {
    enginePromise = (async () => {
      // Lazy import — the only place we pay the ~200KB runtime cost
      const mod = await import("@mlc-ai/web-llm");
      const engine = await mod.CreateMLCEngine(MODEL_ID, {
        initProgressCallback: (report: { progress: number; text: string }) => {
          onProgress(report.progress, report.text);
        },
      });
      engineRef = engine;
      return engine;
    })();
  }
  return enginePromise;
}

// React hook
export function useGenerativeLLM() {
  const [state, setState] = useState<LLMState>({ status: "idle" });
  const cancelRef = useRef<{ cancelled: boolean }>({ cancelled: false });

  // If the engine was already loaded earlier (cached singleton), reflect that
  useEffect(() => {
    if (engineRef && state.status === "idle") {
      setState({ status: "ready", warmed: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    if (state.status === "loading" || state.status === "ready") return;
    setState({ status: "loading", progress: 0, text: "starting" });
    try {
      await getEngine((p, text) => {
        if (!cancelRef.current.cancelled) {
          setState({ status: "loading", progress: p, text });
        }
      });
      setState({ status: "ready", warmed: false });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setState({ status: "failed", error: msg });
    }
  };

  const cancel = () => {
    cancelRef.current.cancelled = true;
    enginePromise = null;
    setState({ status: "idle" });
  };

  const generate = async (
    messages: Message[],
    cb: GenerateCallbacks,
    opts: { maxTokens?: number; temperature?: number } = {},
  ): Promise<void> => {
    if (state.status !== "ready") {
      cb.onError?.("model not ready");
      return;
    }
    const engine = engineRef as {
      chat: {
        completions: {
          create: (req: Record<string, unknown>) => Promise<AsyncIterable<{
            choices: Array<{ delta: { content?: string } }>;
          }>>;
        };
      };
    };
    const t0 = performance.now();
    let firstTokenMs: number | null = null;
    let tokenCount = 0;
    try {
      const stream = await engine.chat.completions.create({
        messages,
        stream: true,
        max_tokens: opts.maxTokens ?? 350,
        temperature: opts.temperature ?? 0.5,
      });
      for await (const chunk of stream) {
        const delta = chunk.choices[0]?.delta?.content;
        if (!delta) continue;
        if (firstTokenMs === null) {
          firstTokenMs = performance.now() - t0;
          cb.onFirstToken?.(firstTokenMs);
        }
        tokenCount++;
        cb.onToken(delta);
      }
      const totalMs = performance.now() - t0;
      cb.onDone?.(totalMs, tokenCount);
    } catch (err) {
      cb.onError?.(err instanceof Error ? err.message : String(err));
    }
  };

  return { state, load, cancel, generate };
}

// Lightweight check: is the model already cached in IndexedDB?
// (Doesn't actually load it — just peeks.)
export async function isModelCached(): Promise<boolean> {
  if (typeof indexedDB === "undefined") return false;
  try {
    const dbs = await indexedDB.databases?.();
    if (!dbs) return false;
    return dbs.some((d) => d.name?.includes("webllm") || d.name?.includes(MODEL_ID.toLowerCase()));
  } catch {
    return false;
  }
}

export const QWEN_MODEL_ID = MODEL_ID;
